import { Component, Prop, State, h, Host } from '@stencil/core';
import { Brand } from '../../types/brand.types';

@Component({
  tag: 'brands-we-love-gallery',
  styleUrl: 'brands-we-love-gallery.css',
  shadow: true,
})
export class BrandsWeLoveGallery {
  /**
   * JSON string or array of brand objects
   */
  @Prop() brands: string | Brand[] = '[]';

  /**
   * Heading text to display above the gallery
   */
  @Prop() heading: string = 'Meet the Creators We Love';

  /**
   * Subtitle text to display above the main heading
   */
  @Prop() subtitle: string = 'FRESH LOOKS, BOLD ROOTS';

  /**
   * If true, show 'Coming Soon' overlay instead of gallery body
   */
  @Prop() comingSoon: boolean = false;

  @State() selectedBrand: Brand | null = null;
  @State() currentImageIndex: number = 0;
  @State() touchStartX: number = 0;
  @State() touchEndX: number = 0;

  private parsedBrands: Brand[] = [];
  private overlayElement: HTMLDivElement | null = null;

  componentWillLoad() {
    this.parsedBrands = this.parseBrands();
  }

  componentWillUpdate() {
    this.parsedBrands = this.parseBrands();
  }

  private parseBrands(): Brand[] {
    if (typeof this.brands === 'string') {
      try {
        return JSON.parse(this.brands);
      } catch (e) {
        console.error('Failed to parse brands JSON:', e);
        return [];
      }
    }
    return this.brands || [];
  }

  private handleBrandClick = (brand: Brand) => {
    this.selectedBrand = brand;
    this.currentImageIndex = 0;
    document.body.style.overflow = 'hidden'; // Prevent background scroll
  };

  private closeOverlay = () => {
    this.selectedBrand = null;
    this.currentImageIndex = 0;
    document.body.style.overflow = ''; // Restore scroll
  };

  private handleOverlayClick = (e: MouseEvent) => {
    // Close only if clicking the overlay background, not the content
    if (e.target === this.overlayElement) {
      this.closeOverlay();
    }
  };

  private nextImage = () => {
    if (!this.selectedBrand) return;
    this.currentImageIndex = (this.currentImageIndex + 1) % this.selectedBrand.galleryImages.length;
  };

  private previousImage = () => {
    if (!this.selectedBrand) return;
    const length = this.selectedBrand.galleryImages.length;
    this.currentImageIndex = (this.currentImageIndex - 1 + length) % length;
  };

  private handleTouchStart = (e: TouchEvent) => {
    this.touchStartX = e.changedTouches[0].screenX;
  };

  private handleTouchEnd = (e: TouchEvent) => {
    this.touchEndX = e.changedTouches[0].screenX;
    this.handleSwipe();
  };

  private handleSwipe = () => {
    const swipeThreshold = 50; // Minimum distance for a swipe
    const diff = this.touchStartX - this.touchEndX;

    if (Math.abs(diff) > swipeThreshold) {
      if (diff > 0) {
        // Swiped left - next image
        this.nextImage();
      } else {
        // Swiped right - previous image
        this.previousImage();
      }
    }
  };

  private handleKeyDown = (e: KeyboardEvent) => {
    if (!this.selectedBrand) return;

    switch (e.key) {
      case 'ArrowLeft':
        this.previousImage();
        break;
      case 'ArrowRight':
        this.nextImage();
        break;
      case 'Escape':
        this.closeOverlay();
        break;
    }
  };

  componentDidLoad() {
    // Add keyboard listener
    document.addEventListener('keydown', this.handleKeyDown);
  }

  disconnectedCallback() {
    // Clean up keyboard listener
    document.removeEventListener('keydown', this.handleKeyDown);
    document.body.style.overflow = ''; // Restore scroll on unmount
  }

  render() {
    return (
      <Host>
        <section class="gallery-section">
          <div class="gallery-header">
            <p class="gallery-subtitle">{this.subtitle}</p>
            <h2 class="gallery-title">{this.heading}</h2>
          </div>

          {this.comingSoon ? (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: '300px',
                width: '100%',
                fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                fontWeight: '700',
                fontSize: '3rem',
                letterSpacing: '0.1em',
                textTransform: 'uppercase',
                color: '#1e293b',
                background: '#f5f5f5',
              }}
            >
              Coming Soon
            </div>
          ) : (
            <div class="brands-grid">
              {this.parsedBrands.map(brand => (
                <div
                  key={brand.id}
                  class="brand-card"
                  onClick={() => this.handleBrandClick(brand)}
                  role="button"
                  tabIndex={0}
                  onKeyPress={(e: KeyboardEvent) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      this.handleBrandClick(brand);
                    }
                  }}
                >
                  <div class="brand-image-container">
                    <img src={brand.thumbnailUrl} alt={`${brand.name} brand`} class="brand-image" loading="lazy" />
                    <div class="brand-overlay-handle">
                      <svg class="instagram-icon" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
                      </svg>
                      <span>@{brand.instagramHandle}</span>
                    </div>
                  </div>
                  <div class="brand-info">
                    <h3 class="brand-name">{brand.name}</h3>
                    <p class="brand-location">
                      {brand.city}, {brand.country}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>

        {this.selectedBrand && !this.comingSoon && (
          <div class="gallery-overlay" onClick={this.handleOverlayClick} ref={el => (this.overlayElement = el as HTMLDivElement)}>
            <div class="overlay-content">
              <button class="close-button" onClick={this.closeOverlay} aria-label="Close gallery">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </button>

              <div class="gallery-container" onTouchStart={this.handleTouchStart} onTouchEnd={this.handleTouchEnd}>
                <button class="nav-button nav-button-prev" onClick={this.previousImage} aria-label="Previous image">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <polyline points="15 18 9 12 15 6"></polyline>
                  </svg>
                </button>

                <div class="gallery-image-wrapper">
                  <img
                    src={this.selectedBrand.galleryImages[this.currentImageIndex]}
                    alt={`${this.selectedBrand.name} - Image ${this.currentImageIndex + 1}`}
                    class="gallery-image"
                  />
                  <div class="gallery-info">
                    <h3>{this.selectedBrand.name}</h3>
                    {this.selectedBrand.brandStory && <p class="brand-story">{this.selectedBrand.brandStory}</p>}
                    <p class="image-counter">
                      {this.currentImageIndex + 1} / {this.selectedBrand.galleryImages.length}
                    </p>
                  </div>
                </div>

                <button class="nav-button nav-button-next" onClick={this.nextImage} aria-label="Next image">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <polyline points="9 18 15 12 9 6"></polyline>
                  </svg>
                </button>
              </div>

              <div class="gallery-dots">
                {this.selectedBrand.galleryImages.map((_, index) => (
                  <button
                    key={index}
                    class={`dot ${index === this.currentImageIndex ? 'active' : ''}`}
                    onClick={() => (this.currentImageIndex = index)}
                    aria-label={`Go to image ${index + 1}`}
                  />
                ))}
              </div>
            </div>
          </div>
        )}
      </Host>
    );
  }
}
